import * as Sentry from "@sentry/react";
import {atom, useAtom, useAtomValue} from "jotai";
import {atomWithStorage} from "jotai/utils";
import {useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {cancelReservationConfirm} from "../../../lib/api.ts";
import {ApiError} from "../../../lib/ApiError.ts";
import {CancelConfirmResponse, CancelFinishResponse} from "../../../lib/types.ts";

export const SignatureAtom = atomWithStorage<Record<string, string>>('cancel_signature', {});

/**
 * Cancel-confirm fetch modeled as a discriminated union so consumers can always
 * distinguish "still loading" from "failed" from "loaded" without a second atom.
 */
export type CancellationState =
    | { status: 'loading' }
    | { status: 'loaded'; data: CancelConfirmResponse }
    | { status: 'error' };

export const CancellationAtom = atom<CancellationState>({ status: 'loading' });
export const CanceledAtom = atom<CancelFinishResponse | null>(null);

export const useSignature = (uuid: string) => {
    const signatures: Record<string, string> = useAtomValue(SignatureAtom);
    return signatures[uuid] ?? null;
}

/** Read saved cancel signatures straight from localStorage, tolerating corrupted JSON. */
const readSavedSignatures = (): Record<string, string> | null => {
    try {
        const saved = localStorage.getItem('cancel_signature');
        return saved ? (JSON.parse(saved) as Record<string, string>) : null;
    } catch {
        return null;
    }
};

export const useCancellation = () => {
    const navigate = useNavigate();
    const {uuid} = useParams();
    const [cancellation, setCancellation] = useAtom(CancellationAtom);

    const signature = useSignature(uuid!);

    useEffect(() => {
        // Reset on every (re-)run so a previous link's status never leaks into a new one.
        setCancellation({status: 'loading'});

        if (!uuid) {
            setCancellation({status: 'error'});
            return;
        }

        if (!signature) {
            // SignatureAtom is atomWithStorage and hydrates after the first render, so a null
            // signature is usually transient. Check localStorage directly (same trick as the
            // route loader) and only flag an error when no signature exists for this uuid.
            if (!readSavedSignatures()?.[uuid]) {
                setCancellation({status: 'error'});
            }
            return;
        }

        const get = async () => {
            try {
                const response = await cancelReservationConfirm(signature, uuid);

                // api.post returns an unrecognised error body (e.g. the 403 for a bad
                // signature, a passed reservation time or a connected check) as if it
                // succeeded, so check for the reservation rather than trusting the call.
                if (response?.reservation) {
                    // Refund amounts (incl. the 5% fee) are computed server-side and
                    // returned in `widget_cancel_choice`; no client-side money math.
                    setCancellation({ status: 'loaded', data: response });
                } else {
                    setCancellation({status: 'error'});
                    Sentry.captureException(new Error('cancel-confirm returned no reservation'), {
                        extra: {
                            uuid
                        }
                    });
                }
            } catch (e: unknown) {
                if (e instanceof ApiError && e.type === 'AlreadyCanceledReservationErrorResponse') {
                    navigate(`./canceled/`);
                } else if (e instanceof Error) {
                    setCancellation({status: 'error'});
                    console.error('Get Reservation Error', e);
                    Sentry.captureException(e, {
                        extra: {
                            uuid
                        }
                    });
                } else {
                    setCancellation({status: 'error'});
                    console.error('Unrecognized Error', e);
                    Sentry.captureException(e, {
                        extra: {
                            uuid
                        }
                    });
                }
            }
        };

        void get();
    }, [signature, uuid]);

    return cancellation
}